import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import ReactDom from 'react-dom';
import { ErrorBoundary } from '../../../../../../ReactSharedScripts/components/ErrorBoundary';
import { listSidebarPortalClass } from '../../../../../../ReactSharedScripts/modules/list/components/ListSidebar/const';
import { pageMainClass, pageSidebarExpandedClass } from '../../../../../../ReactSharedScripts/utils/const';
import { useListSidebarProvider } from '../../../../../../ReactSharedScripts/modules/list/components/ListSidebar/useListSidebarProvider';
import { ListSidebarLayout } from './ListSidebarLayout';
import { useMounted } from '../../../../../../ReactSharedScripts/hooks/useMounted';
import { togglePageMainSidebarTransitionedClass } from '../../../../../../ReactSharedScripts/modules/app/utils';

function getPortal(): HTMLDivElement | null {
	return document.querySelector(`.${listSidebarPortalClass}`);
}

function createPortal() {
	const portal = document.createElement('div');
	portal.classList.add(listSidebarPortalClass);
	document.body.appendChild(portal);

	return portal;
}

function getPageMain(): HTMLDivElement | null {
	return document.querySelector(`.${pageMainClass}`);
}

export const ListSidebarPortal: FC<React.PropsWithChildren> = ({ children }) => {
	const portalRef = useRef<null | HTMLDivElement>(getPortal());
	const [hasPortal, setHasPortal] = useState(portalRef.current !== null);
	const pageMainElementRef = useRef<null | HTMLDivElement>(getPageMain());
	const { isExpanded } = useListSidebarProvider();
	const { mountedRef } = useMounted();

	function togglePageMain(isExpanded) {
		if (pageMainElementRef.current === null) {
			pageMainElementRef.current = getPageMain();
		}

		if (isExpanded) {
			pageMainElementRef.current.classList.add(pageSidebarExpandedClass);
		} else {
			pageMainElementRef.current.classList.remove(pageSidebarExpandedClass);
		}
	}

	useLayoutEffect(() => {
		if (portalRef.current === null) {
			portalRef.current = createPortal();
		}

		setHasPortal(true);

		return () => {
			setHasPortal(false);
		};
	}, []);

	useLayoutEffect(() => {
		setTimeout(() => {
			mountedRef.current && togglePageMainSidebarTransitionedClass(true);
		}, 700);

		return () => {
			togglePageMainSidebarTransitionedClass(false);
		};
	}, [mountedRef]);

	useLayoutEffect(() => {
		togglePageMain(isExpanded);

		return () => {
			togglePageMain(false);
		};
	}, [isExpanded]);

	if (!hasPortal) {
		return null;
	}

	return ReactDom.createPortal(
		<ErrorBoundary>
			<ListSidebarLayout>{children}</ListSidebarLayout>
		</ErrorBoundary>,
		portalRef.current,
	);
};
